import * as React from "react";
import axios from "axios";
import { DateTime } from "luxon";
import * as Ics from "ics";
import fileDownload from 'js-file-download';
import { SuburbAutocomplete } from "./SuburbAutocomplete";
import { PromoThankYouBanner } from "./PromoThankYouBanner";
import { EnquiryContext } from "./context/EnquiryState";

const days = [
	"Sun",
	"Mon",
	"Tue",
	"Wed",
	"Thu",
	"Fri",
	"Sat",
];

declare var c4kCore: any;

export const ThankYouComponent = () => {

	const [showPassword, setShowPassword] = React.useState(false)

	const {
		state,
		dispatch,
		Exit,
		ScrollToTop,
		TrackAnalyticsEvent,
		SendEnquiryUpsells
	} = React.useContext(EnquiryContext)

	React.useEffect(() => {
		dispatch({ type: "SET_FORM_COMPONENT", payload: "thankYou" })

		window.addEventListener('processupsellqueue', (event: CustomEvent) => {
			SendEnquiryUpsells(event.detail);
		});

		TriggerFacebookPixelTracking()
		TrackAnalyticsEvent("submit")
	}, [])

	const SelectSaveDetails = () => {
		let clonedForm = state.form

		CheckEmailAvailability(state.form.email, true)

		clonedForm.password = null
		clonedForm.townSuburb = null
		clonedForm.recapResult = null
		clonedForm.saveDetails = true

		dispatch({ type: "UPDATE_FORM", payload: clonedForm })
		dispatch({ type: "SET_CAN_SUBMIT", payload: false })
	}

	const TriggerFacebookPixelTracking = () => {
		let isPaid = state.model.listingType != "c4k basic"
		let eventType = "general enquiry"
		if (state.form.requestATour) {
			eventType = "request a tour"
		} else if (state.form.liveTourBookingSlot != null) {
			eventType = "live tour booking"
		}
		var evt = new CustomEvent("enquiryfacebookpixeltracking", { detail: { eventType: eventType, isPaid: isPaid } });
		window.dispatchEvent(evt);
	}

	const TrackRegisterAnalyticsEvent = () => {
		const el = document.getElementById("enquiryComponent");
		const calcContext = c4kCore.getGoogleAnalyticsEventContextForElement(el);

		c4kCore.analyticsTrackEventWithSuburbAndListing("Parent Register", "Enquiry", calcContext, el);
	}

	// onLoad is the check thats done when the form loads
	const CheckEmailAvailability = (email, onLoad = false) => {

		dispatch({ type: "SET_CHECKING_EMAIL", payload: true })

		let errors = state.saveErrors
		let clonedForm = state.form

		// Do the availability axios check and then set errors if necessary
		let url = state.model.emailCheckUrl + "?emailAddress=" + email;

		// AXIOS REQUEST
		axios.post(url, {}, { headers: { 'Content-Type': 'application/json' } })
			.then((response: any) => {

				// If email is valid
				if (response.data) {
					errors.email = null
				} else {
					errors.email = { message: "There is already an account for this email" }
				}
				clonedForm.email = email

				dispatch({ type: "SET_CHECKING_EMAIL", payload: false })
				dispatch({ type: "SET_SAVE_ERRORS", payload: errors })
				dispatch({ type: "UPDATE_FORM", payload: clonedForm })

				// If its the on load check then don;t worry about checking canSubmit
				// It will be false from the useEffect when the page loaded
				!onLoad && CheckCanSubmit(clonedForm, errors)
			})
			.catch(error => {

				console.warn('Something went wrong.', error)

				errors.email = {
					message: "Unable to check if email is valid"
				}
				clonedForm.email = email

				dispatch({ type: "SET_CHECKING_EMAIL", payload: false })
				dispatch({ type: "SET_SAVE_ERRORS", payload: errors })
				dispatch({ type: "UPDATE_FORM", payload: clonedForm })

				// If its the on load check then don;t worry about checking canSubmit
				// It will be false from the useEffect when the page loaded
				!onLoad && CheckCanSubmit(clonedForm, errors)
			})
	}

	const HandleEmailChange = (event) => {
		// Check email is valid and then do availability check
		let clonedForm = state.form
		let input = event.target.value
		let errors = state.saveErrors

		// Do validation first and update error if necessary
		let emailPattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		if (input == null || input == '' || !emailPattern.test(input.toLowerCase())) {
			errors.email = { message: "Please enter a valid email address" }
		} else {
			errors.email = null
		}

		// If we passed the validation check then do availability check
		if (errors.email == null) {
			CheckEmailAvailability(input)
		} else {
			clonedForm.email = input
			dispatch({ type: "SET_SAVE_ERRORS", payload: errors })
			dispatch({ type: "UPDATE_FORM", payload: clonedForm })
			CheckCanSubmit(clonedForm, errors)
		}

	}

	// Only for password and suburb fields, email has its own function
	const HandleInputChange = (event, label) => {
		let clonedForm = state.form
		let input = event.target.value
		let errors = state.saveErrors

		let passwordPattern = /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;
		if (label == "password") {
			errors.password = null
			// Check I have a valid password
			if (input == null || input == '' || (label == "password" && !passwordPattern.test(input))) {

				errors.password = { message: "Password requires 8 characters with letters, numbers, and one of @$!%*#?&" }

			}

		} else if (label == "townSuburb") {
			errors.townSuburb = null
			if (input == null || input == '') {
				// Do we have a suburb
				errors.townSuburb = { message: "Please select a suburb" }
			}
		}

		clonedForm[label] = input
		dispatch({ type: "UPDATE_FORM", payload: clonedForm })
		dispatch({ type: "SET_SAVE_ERRORS", payload: errors })

		CheckCanSubmit(clonedForm, errors)
	}

	const SuburbSelectChanged = (value) => {
		let event = {
			target: {
				value: value
			}
		}
		HandleInputChange(event, "townSuburb")
	}

	const CheckCanSubmit = (form, errors) => {
		// All form fields must have a value and no errors
		if ((!!form.email && form.email != "" && errors.email == null) &&
			(!!form.password && form.password != "" && errors.password == null) &&
			(!!form.townSuburb && form.townSuburb != "" && errors.townSuburb == null)) {
			// If we are saving then check password and recaptcha
			dispatch({ type: "SET_CAN_SUBMIT", payload: true })
		} else {
			dispatch({ type: "SET_CAN_SUBMIT", payload: false })
		}
	}

	const ToggleShowPassword = () => {
		// If password is shown hide it
		if (showPassword) {
			//Change input
			document.querySelector('#password-input').setAttribute('type', 'password')
		} else {
			//Show password
			document.querySelector('#password-input').setAttribute('type', 'text')
		}
		setShowPassword(!showPassword)
	}

	const CreateAccount = () => {
		dispatch({ type: "SET_CREATE_ACCOUNT_ERROR", payload: null })
		dispatch({ type: 'TOGGLE_LOADING' })

		let url = state.model?.signUpParentUrl

		let jsonData = {
			name: state.form.parentFullName,
			email: state.form.email,
			password: state.form.password,
			contactNumber: state.form.contactNumber,
			suburb: state.form.townSuburb,
			enquiryType: state.form.requestATour ? 2 : state.initialSelection.id,
		}

		// AXIOS REQUEST
		let headers = {
			'Content-Type': 'application/json',
		}

		const json = JSON.stringify(jsonData)

		// AXIOS REQUEST
		axios.post(url, json, { headers: headers }).then((response: any) => {

			let data = response.data
			if (data.isSuccess) {
				TrackRegisterAnalyticsEvent();
				dispatch({ type: 'TOGGLE_LOADING' })
				dispatch({ type: 'SET_CREATE_ACCOUNT_SUCCESS', payload: true })
				dispatch({ type: 'SET_CAN_SUBMIT', payload: false })

			} else {
				dispatch({
					type: "SET_CREATE_ACCOUNT_ERROR",
					payload: {
						message: data.errors[0]
					}
				})
				dispatch({ type: 'TOGGLE_LOADING' })

			}

		})
			.catch(error => {

				let errorMessage = {
					message: "Something went wrong please try again"
				}

				dispatch({
					type: "SET_CREATE_ACCOUNT_ERROR",
					payload: errorMessage
				})
				dispatch({ type: 'TOGGLE_LOADING' })

			})
	}

	const addToCalendar = () => {
		let bookingSlot: DateTime = state.form.liveTourBookingSlot

		const tourBookingEvent: Ics.EventAttributes = {
			productId: 'CareForKids',
			start: [bookingSlot.year, bookingSlot.month, bookingSlot.day, bookingSlot.hour, bookingSlot.minute],
			duration: { minutes: state.form.liveTourBookingDuration },
			title: `Centre tour - ${state.model.enquiryCentreDetailsList[0].centreName}`,
			description: `Centre tour - ${state.model.enquiryCentreDetailsList[0].centreName}`,
			location: `${state.model.enquiryCentreDetailsList[0].centreAddress}, ${state.model.enquiryCentreDetailsList[0].centreSuburb} ${state.model.enquiryCentreDetailsList[0].centrePostcode}`,
			status: 'CONFIRMED',
			busyStatus: 'BUSY',
			attendees: [
				{ name: state.form.parentFullName, email: state.form.email, rsvp: true, partstat: 'ACCEPTED', role: 'REQ-PARTICIPANT' },
			]
		}

		Ics.createEvent(tourBookingEvent, (error, value) => {
			if (error) {
				//console.log(error)
				dispatch({
					type: "SET_ADD_TO_CALENDAR_ERROR",
					payload: "Failed to generate the calendar file. Please try again."
				})

				ScrollToTop()

				return
			}

			const fileName: string = `live-tour-booking.ics`
			fileDownload(value, fileName)

			dispatch({
				type: "SET_ADD_TO_CALENDAR_ERROR",
				payload: null
			}) 

			TrackAnalyticsEvent("add to calendar")
		})
		 
	}

	const buildLiveTourBookingDetails = () => {
		let timeSlot: DateTime = state.form.liveTourBookingSlot

		return (
			<>
				{state.addToCalendarError != null &&
					<div className="mb-2 text-xs text-red-600">{state.addToCalendarError}</div>
				}

				<p className="mb-3">
					Your live tour booking has been made with <span className="font-bold">{state.model.enquiryCentreDetailsList[0].centreName}</span>.
				</p>

				<div className="my-5 p-4 flex flex-col gap-3 bg-skin-site-header rounded-lg">
					<div className="flex items-centres gap-3">
						<img src="/img/icons/calendar.svg" alt="" className="h4 w-4" />
						<p>{timeSlot.toFormat("EEEE, d MMMM yyyy")}</p>
					</div>
					<div className="flex items-centres gap-3">
						<img src="/img/icons/time.svg" alt="" className="h4 w-4" />
						<p>{timeSlot.toFormat("h:mm a")}</p>
					</div>
					<div className="flex items-centres gap-3">
						<img src="/img/icons/map-pin.svg" alt="" className="h4 w-4" />
						<p>{`${state.model.enquiryCentreDetailsList[0].centreAddress}, ${state.model.enquiryCentreDetailsList[0].centreSuburb} ${state.model.enquiryCentreDetailsList[0].centrePostcode}`}</p>
					</div>
				</div>
				<button className="flex items-center justify-center gap-2 button border border-black text-black mt-3 mx-auto" onClick={addToCalendar}>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M14.5 2.5H1.5C0.947715 2.5 0.5 2.94772 0.5 3.5V14.5C0.5 15.0523 0.947715 15.5 1.5 15.5H14.5C15.0523 15.5 15.5 15.0523 15.5 14.5V3.5C15.5 2.94772 15.0523 2.5 14.5 2.5Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M0.5 6.5H15.5" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M4.5 4V0.5" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M11.5 4V0.5" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M3.75 9C3.70055 9 3.65222 9.01466 3.61111 9.04213C3.57 9.0696 3.53795 9.10865 3.51903 9.15433C3.50011 9.20001 3.49516 9.25028 3.5048 9.29877C3.51445 9.34727 3.53826 9.39181 3.57322 9.42678C3.60819 9.46174 3.65273 9.48555 3.70123 9.4952C3.74972 9.50484 3.79999 9.49989 3.84567 9.48097C3.89135 9.46205 3.9304 9.43 3.95787 9.38889C3.98534 9.34778 4 9.29945 4 9.25C4 9.1837 3.97366 9.12011 3.92678 9.07322C3.87989 9.02634 3.8163 9 3.75 9Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M3.75 12.5C3.70055 12.5 3.65222 12.5147 3.61111 12.5421C3.57 12.5696 3.53795 12.6086 3.51903 12.6543C3.50011 12.7 3.49516 12.7503 3.5048 12.7988C3.51445 12.8473 3.53826 12.8918 3.57322 12.9268C3.60819 12.9617 3.65273 12.9855 3.70123 12.9952C3.74972 13.0048 3.79999 12.9999 3.84567 12.981C3.89135 12.962 3.9304 12.93 3.95787 12.8889C3.98534 12.8478 4 12.7994 4 12.75C4 12.6837 3.97366 12.6201 3.92678 12.5732C3.87989 12.5263 3.8163 12.5 3.75 12.5Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M8 9C7.95055 9 7.90222 9.01466 7.86111 9.04213C7.82 9.0696 7.78795 9.10865 7.76903 9.15433C7.75011 9.20001 7.74516 9.25028 7.7548 9.29877C7.76445 9.34727 7.78826 9.39181 7.82322 9.42678C7.85819 9.46174 7.90273 9.48555 7.95123 9.4952C7.99972 9.50484 8.04999 9.49989 8.09567 9.48097C8.14135 9.46205 8.1804 9.43 8.20787 9.38889C8.23534 9.34778 8.25 9.29945 8.25 9.25C8.25 9.1837 8.22366 9.12011 8.17678 9.07322C8.12989 9.02634 8.0663 9 8 9Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M8 12.5C7.95055 12.5 7.90222 12.5147 7.86111 12.5421C7.82 12.5696 7.78795 12.6086 7.76903 12.6543C7.75011 12.7 7.74516 12.7503 7.7548 12.7988C7.76445 12.8473 7.78826 12.8918 7.82322 12.9268C7.85819 12.9617 7.90273 12.9855 7.95123 12.9952C7.99972 13.0048 8.04999 12.9999 8.09567 12.981C8.14135 12.962 8.1804 12.93 8.20787 12.8889C8.23534 12.8478 8.25 12.7994 8.25 12.75C8.25 12.6837 8.22366 12.6201 8.17678 12.5732C8.12989 12.5263 8.0663 12.5 8 12.5Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M12.25 9C12.2006 9 12.1522 9.01466 12.1111 9.04213C12.07 9.0696 12.038 9.10865 12.019 9.15433C12.0001 9.20001 11.9952 9.25028 12.0048 9.29877C12.0145 9.34727 12.0383 9.39181 12.0732 9.42678C12.1082 9.46174 12.1527 9.48555 12.2012 9.4952C12.2497 9.50484 12.3 9.49989 12.3457 9.48097C12.3914 9.46205 12.4304 9.43 12.4579 9.38889C12.4853 9.34778 12.5 9.29945 12.5 9.25C12.5 9.1837 12.4737 9.12011 12.4268 9.07322C12.3799 9.02634 12.3163 9 12.25 9Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
						<path d="M12.25 12.5C12.2006 12.5 12.1522 12.5147 12.1111 12.5421C12.07 12.5696 12.038 12.6086 12.019 12.6543C12.0001 12.7 11.9952 12.7503 12.0048 12.7988C12.0145 12.8473 12.0383 12.8918 12.0732 12.9268C12.1082 12.9617 12.1527 12.9855 12.2012 12.9952C12.2497 13.0048 12.3 12.9999 12.3457 12.981C12.3914 12.962 12.4304 12.93 12.4579 12.8889C12.4853 12.8478 12.5 12.7994 12.5 12.75C12.5 12.6837 12.4737 12.6201 12.4268 12.5732C12.3799 12.5263 12.3163 12.5 12.25 12.5Z" stroke="#141415" stroke-linecap="round" stroke-linejoin="round" />
					</svg>
					<span>Add to my calendar</span>
				</button>
			</>
		)
	}

	return (
		<React.Fragment>
			<div className="bg-white w-full py-8 md:pt-16">
				<div className="container mx-auto px-6 md:px-0 lg:px-36">

					
					<div className={`grid grid-rows-2 md:grid-cols-2 md:grid-rows-1 md:gap-8 items-center`}>

						{/*Thank You*/}
						<div className="relative">
							{(!!state.model?.closeUrl && state.model?.closeUrl) != "" &&
								<span className="absolute left-2 top-2 cursor-pointer text-skin-heading font-normal text-xl" onClick={() => Exit()}>x</span>
							}
							<div className="mb-4 flex flex-row items-center">
								<img className="h-24 md:h-40 mx-auto" src="/img/shapeicons/calendar.png" alt="calendar" />
							</div>
							<div className="relative text-xl md:text-3xl text-skin-heading font-bold mb-4 text-center">
								Thank you!
							</div>
							{state.initialSelection.id == 1 &&
								<div className="mb-3 md:mb-6 text-sm md:text-base">
									Your enquiry to <a href={state.model.centreListingUrl} className="text-skin-autocomplete-mark underline">{state.model.centreName}</a> has been sent. Centres typically respond within 48 hours. We’ve also sent an email to confirm.
								</div>
							}
							{state.initialSelection.id == 4 && buildLiveTourBookingDetails()}
						</div>

						{/*Save Details*/}
						<div>
							{/*Offer to save details*/}
							{(!state.model.isLoggedIn && !state.form.saveDetails) &&
								<div className="border border-skin-neutral-light rounded-3xl p-6">
									<div className="text-skin-heading text-xl mb-6">Do you want to save your details for next time?</div>
									<div className="flex flex-row gap-4 mb-4">
										<img src="/img/icons/navy-heart-icon.svg" />
										<span>Save your shortlisted items</span>
									</div>
									<div className="flex flex-row gap-4 mb-4">
										<img src="/img/icons/navy-envelope-icon.svg" />
										<span>Keep track of all your waitlists</span>
									</div>
									<div className="flex flex-row gap-4 mb-6">
										<img src="/img/icons/navy-device-icon.svg" />
										<span>Access your account from any device</span>
									</div>
									<button onClick={() => SelectSaveDetails()} className="button cta-secondary w-full">Save my details</button>
								</div>

							}

							{/*Save details fields*/}
							{state.form.saveDetails &&
								<div className="border border-skin-neutral-light rounded-3xl p-6">

									{/*Submit Error*/}
									<div>
										{!!state.createAccountError &&
											<div className="bg-red-200 border border-red-400 text-red-400 p-6 rounded mb-6">
												{state.createAccountError.message}
											</div>
										}
									</div>

									{/*Create Account Form*/}
									{!state.createAccountSuccess &&
										<React.Fragment>
											<div className="mb-6">
												{/*Email field*/}
												<div className="mb-6">
													<label htmlFor="email" className="block text-xs font-medium text-gray-700">EMAIL <span className="text-red-400">*</span></label>
													<div className="mt-1 relative rounded-md shadow-sm">
														<input
															onBlur={() => HandleEmailChange(event)}
															defaultValue={state.form?.email}
															type="email"
															name="email"
															className="enquiry-input"
															placeholder="email@domain.com"
															aria-invalid="true"
															aria-describedby="email-error" />

														{state.checkingEmail &&
															<div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
																{/*Icon*/}
																<img src="/img/navy-spinner.svg" alt="Spinner blue dots circle" height="16" width="16" />
															</div>
														}
													</div>

													{!!state.saveErrors?.email &&
														<p className="mt-2 text-xs text-red-600">{state.saveErrors.email.message}</p>
													}
												</div>

												{/*Password field*/}
												<div className="mb-6">
													<label htmlFor="password" className="block text-xs font-medium text-gray-700">PASSWORD <span className="text-red-400">*</span></label>
													<div className="mt-1 relative rounded-md shadow-sm">
														<input
															onBlur={() => HandleInputChange(event, 'password')}
															id="password-input"
															type="password"
															name="password"
															className="enquiry-input z-10" />

														<button onClick={() => ToggleShowPassword()} className="absolute cursor-pointer inset-y-0 right-0 pr-3 flex items-center z-20">
															{
																showPassword &&
																<img src="/img/icons/wink-closed.png" alt="winking eye" className="pl-1" />
															}
															{
																!showPassword &&
																<img src="/img/icons/wink-open.png" alt="open eye" />
															}
														</button>

													</div>

													{!!state.saveErrors?.password &&
														<div className="mt-2 text-xs text-red-600">{state.saveErrors.password.message}</div>
													}
												</div>

												<div className="mb-8">
													<label htmlFor="suburb" className="block text-xs font-medium text-gray-700">SUBURB <span className="text-red-400">*</span></label>
													<SuburbAutocomplete
														handleSelect={(value) => SuburbSelectChanged(value)}
													/>
													{!!state.saveErrors?.townSuburb &&
														<div className="mt-2 text-xs text-red-600">{state.saveErrors.townSuburb.message}</div>
													}
												</div>

											</div>
											<button onClick={() => CreateAccount()} className="button cta-primary w-full disabled:opacity-50 flex flex-row items-center justify-center gap-2" disabled={state.loading || !state.canSubmit}>
												{state.loading && <span className="flex flex-row items-center gap-2"><img src="/img/white-spinner.svg" alt="Spinner white dots circle" /><span>Creating...</span></span>}
												{!state.loading && <span>Create Account</span>}
											</button>
										</React.Fragment>
									}

									{/*Account created succesfully*/}
									{state.createAccountSuccess &&
										<div>
											<div className="mb-4 text-skin-heading text-2xl">Success!</div>
											<div>
												Your account has been created, please follow the activation steps sent to: <span className="font-bold">{state.form.email}</span>
											</div>
										</div>
									}
								</div>
							}
						</div>

						{/*image*/}
						{/*<div className="w-full h-auto block mb-4">*/}
						{/*	<div className="overflow-hidden h-52 lg:h-64 w-full rounded-3xl">*/}
						{/*		<img className="w-full object-cover object-bottom h-52 lg:h-64" src={`/img/enquiry/enquiry-thank-you${state.model.isLoggedIn ? '-sm' : ''}.jpg`} alt="1" />*/}
						{/*	</div>*/}
						{/*</div>*/}

					</div>

					{/*Promo Banner*/}
					{state.model?.freeChildCarePromo && 
						<PromoThankYouBanner />
					}
					
				</div>
			</div>
		</React.Fragment>
	);

}